import React, { useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  FileText, Loader2, CheckCircle, Sparkles, Send, Square,
  AlertCircle, Wand2, ChevronRight, BarChart3, Table, PieChart, LineChart, Type, Image as ImageIcon
} from 'lucide-react';

const vizTypeIcons = {
  bar_chart: BarChart3, line_chart: LineChart, pie_chart: PieChart,
  table: Table, kpi: BarChart3, rich_text: Type, image: ImageIcon, shape: Square,
};

export function ImportChatView({
  fileName, pageCount, analysis, messages, inputMessage, setInputMessage,
  sending, status, onSendMessage, onGenerateReport, onReset
}) {
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSendMessage();
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* File info bar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg mb-3">
        <FileText className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium">{fileName}</span>
        <Badge variant="secondary" className="text-xs">{pageCount} page{pageCount > 1 ? 's' : ''}</Badge>
        {analysis?.confidence_score && (
          <Badge variant="outline" className="text-xs ml-auto">
            {Math.round(analysis.confidence_score * 100)}% confidence
          </Badge>
        )}
      </div>

      <div className="flex-1 flex gap-4 overflow-hidden">
        {/* Detected Elements Panel */}
        <div className="w-48 flex-shrink-0 border rounded-lg overflow-hidden flex flex-col">
          <div className="px-3 py-2 bg-muted/30 border-b">
            <span className="text-xs font-semibold uppercase text-muted-foreground">Detected Elements</span>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {(analysis?.visuals || []).map((v, idx) => {
                const Icon = vizTypeIcons[v.type] || Square;
                return (
                  <div key={v.title || `visual-${idx}`} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/20 hover:bg-muted/40">
                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs truncate">{v.title || v.type}</span>
                  </div>
                );
              })}
              {(!analysis?.visuals || analysis.visuals.length === 0) && (
                <p className="text-xs text-muted-foreground p-2">No elements detected yet</p>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden border rounded-lg">
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div key={msg.timestamp || `msg-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-lg px-4 py-2.5 ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : msg.isError ? 'bg-destructive/10 border border-destructive/20'
                      : msg.isQuestion ? 'bg-amber-500/10 border border-amber-500/20'
                      : 'bg-muted/50'
                  }`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-primary" />
                        <span className="text-xs font-medium text-primary">AI Assistant</span>
                      </div>
                    )}
                    <ChatMessageContent msg={msg} />
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-muted/50 rounded-lg px-4 py-2.5">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>

          {/* Chat Input */}
          <div className="border-t p-3 flex gap-2 items-end">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask questions or provide clarifications... (Cmd+Enter to send)"
              disabled={sending || status === 'generating'}
              className="flex-1 min-h-[76px] max-h-[240px] rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              rows={3}
              data-testid="import-chat-input"
            />
            <Button size="icon" onClick={onSendMessage} disabled={!inputMessage.trim() || sending || status === 'generating'}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mt-4 pt-4 border-t">
        <Button variant="outline" onClick={onReset} disabled={status === 'generating'}>Start Over</Button>
        <div className="flex gap-2">
          {analysis?.questions?.length > 0 && (
            <Badge variant="secondary" className="gap-1">
              <AlertCircle className="w-3 h-3" />
              {analysis.questions.length} question{analysis.questions.length > 1 ? 's' : ''} remaining
            </Badge>
          )}
          <Button onClick={onGenerateReport} disabled={status === 'generating'} className="gap-2" data-testid="generate-report-btn">
            {status === 'generating' ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
            ) : (
              <><Wand2 className="w-4 h-4" /> Create Report <ChevronRight className="w-4 h-4" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChatMessageContent({ msg }) {
  if (msg.role !== 'assistant' || !msg.content) {
    return <div className="text-sm whitespace-pre-wrap">{msg.content}</div>;
  }

  const hasJson = msg.content.includes('```json') || msg.content.includes('```\n{') || (/^\s*\{[\s\S]*"visuals"[\s\S]*\}\s*$/.test(msg.content.trim()));
  if (!hasJson) {
    return <div className="text-sm whitespace-pre-wrap">{msg.content}</div>;
  }

  const parts = msg.content.split(/(```json[\s\S]*?```|```[\s\S]*?```)/);
  const textParts = parts.filter(p => !p.startsWith('```'));
  const jsonParts = parts.filter(p => p.startsWith('```'));
  const preText = textParts.join('').trim();

  return (
    <div className="space-y-2">
      {preText && <div className="text-sm whitespace-pre-wrap">{preText}</div>}
      <div className="flex items-center gap-2 p-2.5 rounded-lg bg-green-500/10 border border-green-500/20">
        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
        <span className="text-sm font-medium text-green-600 dark:text-green-400">Report configuration is ready. Click &quot;Create Report&quot; below to build it.</span>
      </div>
      {jsonParts.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none py-1">View report definition JSON</summary>
          <pre className="mt-1 p-2 rounded bg-muted/30 overflow-auto max-h-48 text-[10px] text-muted-foreground">{jsonParts.map(p => p.replace(/^```json?\n?/, '').replace(/\n?```$/, '')).join('\n')}</pre>
        </details>
      )}
    </div>
  );
}
